import * as React from "react"
import { Head, Link, usePage } from "@inertiajs/react"
import { useIntl } from "react-intl"
import { Layers, Plus, Repeat, Sparkles } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { PageHeader } from "@/components/PageHeader"
import { EmptyState } from "@/components/blocks/EmptyState"
import { DeckFormDialog } from "@/components/flashcards/DeckFormDialog"
import { DeckList } from "@/components/flashcards/DeckList"
import { UnlockPrompt } from "@/components/UnlockPrompt"
import { Button } from "@/components/ui/button"
import type { PageProps } from "@/types/inertia"
import type { DeckSummary } from "@/types/flashcards"

export default function FlashcardsIndex({
  due_count,
  official_decks,
  my_decks,
}: {
  due_count: number
  official_decks: DeckSummary[]
  my_decks: DeckSummary[]
}) {
  const intl = useIntl()
  const [creating, setCreating] = React.useState(false)
  const programAccess = usePage<PageProps>().props.current_user?.program_access ?? false
  const deckHref = (deck: DeckSummary) => `/decks/${deck.id}`

  return (
    <>
      <Head title={intl.formatMessage({ id: "flashcards.title" })}>
        <meta name="description" content={intl.formatMessage({ id: "flashcards.index_meta" })} />
        <meta property="og:title" content={intl.formatMessage({ id: "flashcards.title" })} />
        <meta property="og:description" content={intl.formatMessage({ id: "flashcards.index_meta" })} />
      </Head>
      <AppShell>
        <PageHeader
          title={intl.formatMessage({ id: "flashcards.title" })}
          description={programAccess ? intl.formatMessage({ id: "flashcards.subtitle_program" }) : intl.formatMessage({ id: "flashcards.subtitle_general" })}
          actions={
            <>
              <Button type="button" variant="secondary" onClick={() => setCreating(true)}>
                <Plus className="h-4 w-4" /> {intl.formatMessage({ id: "flashcards.new_deck" })}
              </Button>
              <Button asChild variant="secondary">
                <Link href="/generations/new?kind=deck">
                  <Sparkles className="h-4 w-4" /> {intl.formatMessage({ id: "flashcards.generate_ai" })}
                </Link>
              </Button>
              {due_count > 0 ? (
                <Button asChild>
                  <Link href="/review">
                    <Repeat className="h-4 w-4" /> {intl.formatMessage({ id: "flashcards.review_n" }, { count: due_count })}
                  </Link>
                </Button>
              ) : (
                <Button type="button" disabled title={intl.formatMessage({ id: "flashcards.none_due_tip" })}>
                  <Repeat className="h-4 w-4" /> {intl.formatMessage({ id: "flashcards.review" })}
                </Button>
              )}
            </>
          }
        />
        <p className="mt-4 text-sm text-ink-muted">
          {due_count > 0
            ? intl.formatMessage({ id: "flashcards.due_text" }, { count: due_count })
            : intl.formatMessage({ id: "flashcards.nothing_due" })}
        </p>

        {(programAccess || official_decks.length > 0) && (
          <section className="mt-10">
            <h2>{intl.formatMessage({ id: "flashcards.official_decks" })}</h2>
            {official_decks.length === 0 ? (
              <p className="mt-2">{intl.formatMessage({ id: "flashcards.official_soon" })}</p>
            ) : (
              <div className="mt-4">
                <DeckList decks={official_decks} hrefFor={deckHref} />
              </div>
            )}
          </section>
        )}

        <section className="mt-10">
          <h2>{intl.formatMessage({ id: "flashcards.my_decks" })}</h2>
          {my_decks.length === 0 ? (
            <div className="mt-4">
              <EmptyState
                icon={Layers}
                title={intl.formatMessage({ id: "flashcards.empty_title" })}
                description={intl.formatMessage({ id: "flashcards.empty_body" })}
                action={
                  <Button type="button" onClick={() => setCreating(true)}>
                    <Plus className="h-4 w-4" /> {intl.formatMessage({ id: "flashcards.new_deck" })}
                  </Button>
                }
              />
            </div>
          ) : (
            <div className="mt-4">
              <DeckList decks={my_decks} hrefFor={deckHref} />
            </div>
          )}
        </section>

        {!programAccess && official_decks.length === 0 && (
          <section className="mt-10">
            <h2>{intl.formatMessage({ id: "flashcards.official_decks" })}</h2>
            <UnlockPrompt what={intl.formatMessage({ id: "flashcards.unlock_what" })} />
          </section>
        )}

        <DeckFormDialog
          open={creating}
          onOpenChange={setCreating}
          action="/decks"
          method="post"
          title={intl.formatMessage({ id: "flashcards.new_deck_title" })}
          description={intl.formatMessage({ id: "flashcards.new_deck_description" })}
          submitLabel={intl.formatMessage({ id: "flashcards.create_deck" })}
        />
      </AppShell>
    </>
  )
}
